import React, { useState, useRef, useEffect } from 'react'

import Field from '../../Field/Field'
import TextInput from '../../TextInput/TextInput'
import Textarea from '../../Textarea/Textarea'
import Tooltip from '../../Tooltip/Tooltip'
import FieldLabel from '../../FieldLabel/FieldLabel'
import Help from '../../Help/Help'
import { fieldUidRegex, getContentTypeFieldDataTestId, CONTENT_TYPE_FIELD_ACTION_IDS, NonLocalizableDom, handleToggleClick, handleMarkParentNonLocalizable, handleTabToggle, ToggleSwitchTab } from '../helper'
import ToggleSwitch from '../../ToggleSwitch/ToggleSwitch'
import CustomUserLimit from './CustomUserLimit'
import MandatoryToggleSwitch from './MandatoryToggleSwitch'
import { booleanTestIdFormatter } from './../../../utils/helpers'

export const ModularBlocks = (props: any) => {
  const { onChange, options, parentInfo, uniqueKey, schema, type, isNonLocalizable, hasNonLocalizablePlan, userCustomFieldLimits = { hasUserCustomFieldLimit: false, hasUserCustomFieldLimitRestFields: false }, hasNonLocalizablePlanForBlocksAndGroupMultiple } = props
  const [initialSchema, setSchema] = useState(schema)
  const { display_name, uid, field_metadata } = schema
  const [error, setError] = useState(fieldUidRegex(schema.uid, schema))

  const displayNameRef: any = useRef(null)
  const { hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields } = userCustomFieldLimits
  useEffect(() => {
    displayNameRef?.current?.focus()
  }, [displayNameRef])

  const onchange = () => {
    onChange && onChange(schema)
    setSchema({ ...schema })
  }
  const onDisplayNameChange = (event: any) => {
    schema.display_name = event.target.value

    if (schema.hasOwnProperty('display_name') && schema.newfield) {
      schema.uid = schema.display_name
        .trim()
        .replace(/[^A-Z0-9]+/gi, '_')
        .toLowerCase()
      if (schema.display_name) {
        schema.error_details = {
          ...schema.error_details,
          uid: ''
        }
      }
    }

    if (!schema.error_details) {
      schema.error_details = {}
    }

    if (!schema.display_name.length) {
      schema.error_details = {
        ...schema.error_details,
        display_name: 'Display Name cannot be empty'
      }
      schema.error = true
    } else {
      schema.error_details = {
        ...schema.error_details,
        display_name: ''
      }
      schema.error = Object.keys(schema.error_details).filter((fi) => schema.error_details[fi].length).length
        ? true
        : false
    }
    onchange()
  }
  const onUidChange = (event: any) => {
    setError(fieldUidRegex(event.target?.value?.trim(), schema))

    if (!schema.error_details) {
      schema.error_details = {}
    }

    if (!schema.uid.length) {
      schema.error_details = {
        ...schema.error_details,
        uid: 'uid cannot be empty'
      }
      schema.error = true
    } else {
      schema.error_details = {
        ...schema.error_details,
        uid: ''
      }
      schema.error = Object.keys(schema.error_details).filter((fi) => schema.error_details[fi].length).length
        ? true
        : false
    }
    onchange()
  }
  const onHelpTextChange = (event: any) => {
    field_metadata.description = event.target.value
    onchange()
  }
  const onOptionChange = (event: any) => {
    if (event.target.name === `${uniqueKey}.mandatory`) {
      schema.mandatory = event.target.checked
    }
    if (event.target.name === `${uniqueKey}.nonLocalization`) {
      if (event.target.checked && hasNonLocalizablePlanForBlocksAndGroupMultiple && !parentInfo?.isInsideMultiple) {
        handleMarkParentNonLocalizable(schema, onchange)
      }
      else if (event.target.checked && parentInfo.hasParent && hasNonLocalizablePlanForBlocksAndGroupMultiple) {
        handleToggleClick(event, parentInfo.data_type, schema, onchange, 'nonLocalizable', props.t, parentInfo.isInsideMultiple, parentInfo.ancestorWithMultipleType)
      }
      else {
        schema.non_localizable = event.target.checked
      }
    }
    if(event.target.name === `${uniqueKey}.tab`) {
      handleTabToggle(event, schema)
    }
    onchange()
  }

  if (type === 'basic') {
    return (
      <div className="FormFields">

        <Field>
          <FieldLabel className='FieldLabel__CT' testId={`${getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.DISPLAY_NAME_INPUT)}-label`} version='v2' required htmlFor="displayName">
            Display Name
          </FieldLabel>
          <TextInput
            version='v2'
            required
            name={`${uniqueKey}.displayName`}
            value={display_name}
            onChange={onDisplayNameChange}
            inputRef={displayNameRef}
            error={schema.error && schema.error_details.display_name ? true : false}
            placeholder={props.t(
              'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
            )}
            testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.DISPLAY_NAME_INPUT)}
          />
        </Field>
        <Field>
          <FieldLabel className='FieldLabel__CT' testId={`${getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.UID_INPUT)}-label`} version='v2' required htmlFor="uid">
            Unique ID
          </FieldLabel>
          <Help type="basic" testId={`${getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.UID_INPUT)}-help-text`} text="A unique ID for this field. This should not be same as the ID of any other field in this content type." />
          <TextInput
            version='v2'
            required
            name={`${uniqueKey}.uid`}
            value={uid}
            onChange={onUidChange}
            error={(schema.error && schema.error_details.uid) || error ? true : false}
            placeholder={props.t(
              'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
            )}
            testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.UID_INPUT)}
          />
        </Field>
        <Field>
          <FieldLabel className='FieldLabel__CT' testId={`${getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.HELP_TEXT_INPUT)}-label`} version='v2' htmlFor="helpText">Help Text</FieldLabel>
          <Help type="basic" testId={`${getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.HELP_TEXT_INPUT)}-help-text`} text="Provide additional info about the field. The help text will appear when the user hovers on the ‘?’ sign beside the field name." />
          <Textarea
            version='v2'
            name={`${uniqueKey}.helpText`}
            value={field_metadata.description || ''}
            onChange={onHelpTextChange}
            placeholder={props.t(
              'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
            )}
            // multiline={true}
            testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.HELP_TEXT_INPUT)}
            id={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.HELP_TEXT_INPUT)}
          />
        </Field>
      </div>
    )
  } else if (type === 'advanced') {
    return (
      <div className="FormFields FormFields__blocks">
        <Field className='FieldLabel__CT LastField'>
          {
            hasUserCustomFieldLimit && (
              <FieldLabel testId='cs-ctf-modularblock-advanced-options' version='v2' htmlFor="options">Options</FieldLabel>
            )
          }
          <div className="FormFields__selection-wrapper">
            {hasUserCustomFieldLimit && (
              <MandatoryToggleSwitch
                uniqueKey={uniqueKey}
                schema={schema}
                onChange={onOptionChange}
              />
            )}
            {hasUserCustomFieldLimit ? <CustomUserLimit
              uniqueKey={uniqueKey}
              schema={schema}
              onchange={onchange}
              t={props.t}
              type={type}
            /> : null}
            <div className={`FormFields`}>
              {
                hasNonLocalizablePlan ? (
                  hasNonLocalizablePlanForBlocksAndGroupMultiple ? (
                    <NonLocalizableDom
                      isFieldPresent={hasUserCustomFieldLimit}
                      uniqueKey={uniqueKey}
                      schema={schema}
                      onOptionsChange={onOptionChange}
                      dataTestId={booleanTestIdFormatter(schema.nonLocParams?.insideMultiple ? `cs-ct-field-modular-blocks-non-localizable-parent-multiple` : `cs-ct-field-modular-blocks-non-localizable`, schema.non_localizable)}
                      dataTestIdForDescription={"cs-ct-field-modular-blocks-non-localizable-description"}
                      isNonLocalizable={isNonLocalizable}
                      userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields}}
                      isAncestorNonLoc={parentInfo.isAncestorNonLoc}
                    />
                  ) : (parentInfo.hasParent ? !parentInfo.isInsideMultiple : true) ?
                    <NonLocalizableDom
                      isFieldPresent={hasUserCustomFieldLimit}
                      uniqueKey={uniqueKey}
                      schema={schema}
                      onOptionsChange={onOptionChange}
                      dataTestId={booleanTestIdFormatter("cs-ct-field-modularblocks-non-localizable", schema.non_localizable)}
                      dataTestIdForDescription={"cs-ct-field-modular-blocks-non-localizable-description"}
                      isNonLocalizable={isNonLocalizable}
                      userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields}}
                      isAncestorNonLoc={parentInfo.isAncestorNonLoc}
                    />
                    : null
                ) : null
              }
             {
          //   schema.shouldDisplayTypeTab ? <div className={`ToggleWrap ${hasUserCustomFieldLimit ? 'flex-v-center' : ''}`}>
          //    <ToggleSwitch
          //      testId={booleanTestIdFormatter("cs-ct-field-group-multiple", schema.multiple)}
          //      name={`${uniqueKey}.tab`}
          //      label="Show as Tab"
          //      checked={schema.field_metadata?.isTab}
          //      onClick={onOptionChange}
          //    />
          //    {hasUserCustomFieldLimit &&
          //      <div>
          //        <Help type="basic" className='mb-0 ml-8' iconName='Information' text={props.t('content_type.save_content_type.body.fields.tooltip_text.multiple') || "If enabled, you can define the minimum and maximum instances allowed for the field"} />
          //      </div>
          //    }
          //  </div> : null

          schema.shouldDisplayTypeTab ? <ToggleSwitchTab uniqueKey={uniqueKey} fieldType={'blocks'} isTab={schema.field_metadata?.isTab || false} t={props.t} onOptionsChange={onOptionChange} /> : null

          }
            </div>
          </div>
        </Field>
      </div>

    )
  }
}

export default React.memo(ModularBlocks)